/**
 * Responsive image size override
 *
 * Allows CSS-only sites to control which srcset image is loaded by setting
 * the --responsive-image-size CSS custom property on the image. Supports CSS
 * media queries: the property is re-read on viewport resize.
 *
 * Usage:
 *   .page-hero__image img {
 *     --responsive-image-size: 100vw;
 *   }
 *   @media (min-width: 740px) {
 *     .page-hero__image img {
 *       --responsive-image-size: 50vw;
 *     }
 *   }
 */

import g from '#core/global';
import load from '#core/load';
import util from '#core/util';


const image_$ = 'img.media-figure__source';
const size_prop = '--responsive-image-size';
const applied_attr = 'data-sizes-applied';

let has_images = false;


const module = {
	
	/**
	 * Init module
	 */
	init() {
		if (load.ran('image-bare')) return false;
		
		load.content(() => {
			has_images = assign_elements();
			if (!has_images) return false;
		});
		load.all(() => {
			has_images = assign_elements();
			if (!has_images) return false;
			
			assign_resize_events();
			assign_mutation_events();
		});
	},
	
};

export default module;
module.init();


/**
 * Assign elements and apply initial overrides
 * @return {boolean}
 */
function assign_elements() {
	if (has_images) return true;
	
	const $images = document.querySelectorAll(image_$);
	if (!$images.length) return false;
	
	$images.forEach($image => {
		apply_override($image);
	});
	
	return true;
}


/**
 * Assign resize events
 */
function assign_resize_events() {
	window.addEventListener(g.resize_e, util.debounce(() => {
		process_all(true);
	}, 150));
}


/**
 * Assign DOM change events
 */
function assign_mutation_events() {
	const observer = new MutationObserver(mutations => {
		for (const mutation of mutations) {
			if (mutation.type !== 'childList') continue;
			
			mutation.addedNodes.forEach($node => {
				if ($node.nodeType !== Node.ELEMENT_NODE) return;
				
				// Check if the node itself is an image
				if ($node.matches?.(image_$)) {
					apply_override($node);
				}
				
				// Check children
				$node.querySelectorAll?.(image_$).forEach($image => {
					apply_override($image);
				});
			});
		}
	});
	
	observer.observe(document.body, {
		childList: true,
		subtree: true,
	});
}


/**
 * Process all images
 * @param {boolean} is_resize
 */
function process_all(is_resize = false) {
	document.querySelectorAll(image_$).forEach($image => {
		apply_override($image, is_resize);
	});
}


/**
 * Apply size override to image
 * @param {Element} $image
 * @param {boolean} is_resize
 */
function apply_override($image, is_resize = false) {
	const override = getComputedStyle($image).getPropertyValue(size_prop).trim();
	
	// No override set: skip on initial load, nothing to do on resize
	if (!override) {
		if (!is_resize) {
			$image.setAttribute(applied_attr, g.false_s);
		}
		return;
	}
	
	// Only update if value changed (or first time)
	if ($image.sizes !== override) {
		$image.sizes = override;
	}
	
	$image.setAttribute(applied_attr, g.true_s);
}
